<script setup lang="ts">
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import type { CurrentQuestion } from "~/types/quiz.types";

const { course } = useCourseState();
const { t } = useI18n();
const route = useRoute();

const questions = ref<CurrentQuestion[]>();
const currentQuestionIndex = ref(0);
const isLoading = ref(true);

onBeforeMount(async () => {
  const url = useApiUrl(`/courses/${route.params.courseId}/kickoff`);
  const response = await useAuthenticatedFetch<CurrentQuestion[]>(url);
  if (response.data.value) {
    questions.value = response.data.value;
  }

  isLoading.value = false;
});
</script>

<template>
  <div
    v-if="isLoading"
    class="flex items-center justify-center h-full"
  >
    <Loader />
  </div>

  <div v-else-if="course && questions">
    <div class="bg-white p-4 flex items-center justify-between">
      <Button
        text
        :disabled="currentQuestionIndex === 0"
        severity="secondary"
        class="text-16 font-bold text-neutral-80 px-3 py-2"
        @click="currentQuestionIndex--"
      >
        <div class="flex items-center gap-2">
          <FontAwesomeIcon icon="fa-chevron-left" />
          <span>{{t('course.sidebar.tabs.kickoff.navigation.previous')}}</span>
        </div>
      </Button>

      <div class="text-18 font-bold text-black">
        {{t('course.sidebar.tabs.kickoff.navigation.question')}} {{currentQuestionIndex + 1}}/{{questions.length}}
      </div>

      <Button
        text
        :disabled="currentQuestionIndex === questions.length - 1"
        severity="secondary"
        class="text-16 font-bold text-neutral-80 px-3 py-2"
        @click="currentQuestionIndex++"
      >
        <div class="flex items-center gap-2">
          <span>{{t('course.sidebar.tabs.kickoff.navigation.next')}}</span>
          <FontAwesomeIcon icon="fa-chevron-right" />
        </div>
      </Button>
    </div>

    <div class="flex flex-col gap-6 py-6 px-4">
      <div>
        <div class="text-neutral-60 tracking-wide text-14 font-bold uppercase ">
          {{t('course.sidebar.tabs.kickoff.content.question')}} #{{currentQuestionIndex + 1}}
        </div>

        <div
          v-sanitize-html="questions[currentQuestionIndex].question.question"
          class="mt-3 text-18 font-bold text-neutral-100"
        />

        <QuizMedia
          v-if="questions?.[currentQuestionIndex]?.question?.questionMedia"
          :key="questions[currentQuestionIndex].questionId"
          :media="questions[currentQuestionIndex].question.questionMedia"
          :video-loop-enabled="questions[currentQuestionIndex]?.question?.videoLoopEnabled"
          class="mt-4"
        />
      </div>

      <div>
        <div class="flex flex-col gap-4">
          <QuizAnswers
            :current-question="questions[currentQuestionIndex]"
            readonly
            hide-title
          />
        </div>
      </div>

      <!-- fixme: should be inside QuizAnswers component -->
      <div
        v-if="questions[currentQuestionIndex].isCorrect"
        class="bg-green-60 text-white py-1.5 rounded-md text-center font-bold text-12 tracking-wide uppercase"
      >
        {{t('course.sidebar.tabs.kickoff.content.result.wellDone')}}
      </div>
      <div
        v-else
        class="bg-red-50 text-white py-1.5 rounded-md text-center font-bold text-12 tracking-wide uppercase"
      >
        {{t('course.sidebar.tabs.kickoff.content.result.incorrect')}}
      </div>
    </div>
  </div>
</template>
